/**
 * @file StatementHandOverDialog.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-10-12
 * @modified 2026-10-19
 *
 * "Hand over…" on Generate Statements: give the statement run, at whatever
 * stage it has reached, to one or more colleagues. Everything done so far is
 * kept on the run, so it goes across with it.
 *
 * Choices: who (colleagues who can run statements — the server's list), an
 * optional message, "Keep me on this run" (off: it moves to them and leaves
 * this screen; on: shared, you both work on it) and "Email them as well"
 * (on by default — the notice on Generate Statements is only seen by someone
 * who opens that screen).
 *
 * `mode="accounts"` (1.1.0) is "Hand over to Accounts": the same window and
 * choices — whichever colleague the person picks — titled for Accounts, the
 * message started as "Statements approved — please check them before they go
 * out." and the run marked as with Accounts when it goes (the caller sends
 * toAccounts).
 *
 * Mount it only while it is open (the slide-out buttons are measured when it
 * mounts).
 */

import React, { useEffect, useState } from 'react';
import { API_URL } from '../config';
import Modal from './Modal';
import Textarea from './Textarea';
import TickCheckbox from './TickCheckbox';
import LoadingSpinner from './LoadingSpinner';
import { useSlideOutButton } from './SlideOutButton';
import './StatementHandOver.css';

const MAX_MESSAGE = 1000;

/**
 * @param {object} props
 * @param {string} props.subject      what is being handed over, e.g. "the September 2026 statements"
 * @param {string} props.stage        where it has got to, e.g. "Step 2 — Generate & approve"
 * @param {Array}  [props.people]     who is on the run now ({ userId, name })
 * @param {function} props.onHandOver ({ toUserIds, message, keepMe, emailThem }) → Promise<{ ok, error? }>
 * @param {function} props.onClose
 * @param {'handover'|'accounts'} [props.mode]
 * @param {string} [props.initialMessage]   the message box's starting text
 * @param {string} [props.confirmationText] accounts mode: what Accounts will confirm
 */
function StatementHandOverDialog({ subject, stage, people = [], onHandOver, onClose, mode = 'handover', initialMessage = '', confirmationText = '' }) {
  const toAccounts = mode === 'accounts';
  const [colleagues, setColleagues] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [chosen, setChosen] = useState(new Set());
  const [message, setMessage] = useState(initialMessage || '');
  const [keepMe, setKeepMe] = useState(false);
  const [emailThem, setEmailThem] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/statements/work/colleagues`, { credentials: 'include' });
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
        setColleagues(data.colleagues || []);
      } catch (err) {
        if (!cancelled) setLoadError(`Could not load your colleagues: ${err.message}`);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useSlideOutButton([saving, chosen.size, keepMe]);

  const onRun = new Set(people.map(p => p.userId));
  const toggle = (id) => setChosen(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const blockReason = saving ? 'Handing over…'
    : !chosen.size ? 'Choose at least one colleague'
      : null;

  const submit = async () => {
    if (blockReason) return;
    setSaving(true);
    setError(null);
    const result = await onHandOver({ toUserIds: [...chosen], message: message.trim(), keepMe, emailThem });
    setSaving(false);
    if (result && result.ok === false) setError(result.error || 'It could not be handed over.');
  };

  return (
    <Modal
      isOpen
      onClose={saving ? () => {} : onClose}
      title={toAccounts ? 'Hand over to Accounts' : 'Hand over statements'}
      size="md"
      className="statement-handover"
      footer={
        <div className="buttons slideout">
          <div>
            <button className="btn-slideout" onClick={onClose} disabled={saving} data-read-safe>
              <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
              <span className="btn-label">Cancel</span>
            </button>
          </div>
          <div>
            <button className="btn-slideout" onClick={submit} disabled={!!blockReason} title={blockReason || (keepMe ? 'Share it — you stay on it' : 'Hand it over — it leaves your screen')}>
              <div className="icon-wrapper">
                <span className={`material-symbols-rounded${saving ? ' spinning' : ''}`}>{saving ? 'progress_activity' : (toAccounts ? 'fact_check' : 'handshake')}</span>
              </div>
              <span className="btn-label">{saving ? 'Handing over…' : toAccounts ? (keepMe ? 'Share with Accounts' : 'Hand over to Accounts') : (keepMe ? 'Share' : 'Hand over')}</span>
            </button>
          </div>
        </div>
      }
    >
      <div className="handover-body">
        {toAccounts ? (
          <p className="handover-intro">
            Hand <strong>{subject}</strong> ({stage}) to Accounts to check before they go out. Accounts review
            the approvals and notes already made — approving, unapproving or adding notes as needed — check
            the flags, then confirm <q>{confirmationText}</q>. Emailing customers and printing wait for that.
            Everything done so far goes with it.
          </p>
        ) : (
          <p className="handover-intro">
            Hand over <strong>{subject}</strong> ({stage}). Everything done so far goes with it: the step it
            is at, the contacts, approvals and notes, which customers' invoices are bundled, the delivery
            choices and any extra PDFs.
          </p>
        )}

        <div className="handover-section">
          <h4>{toAccounts ? 'Who in Accounts' : 'Hand it to'}</h4>
          {loadError ? (
            <div className="handover-outcome blocked" role="alert">
              <span className="material-symbols-rounded">error</span><span>{loadError}</span>
            </div>
          ) : colleagues === null ? (
            <LoadingSpinner />
          ) : colleagues.length === 0 ? (
            <p className="handover-hint">Nobody else can run statements at the moment.</p>
          ) : (
            <div className="handover-people" role="group" aria-label="Colleagues">
              {colleagues.map(c => (
                <div key={c.id} className={`handover-person${chosen.has(c.id) ? ' chosen' : ''}`}>
                  <TickCheckbox
                    id={`handover-person-${c.id}`}
                    checked={chosen.has(c.id)}
                    onChange={() => toggle(c.id)}
                    disabled={saving}
                    label={c.name}
                  />
                  <span className="handover-person-email">{c.email}</span>
                  {onRun.has(c.id) && <span className="handover-tag">already on this run</span>}
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="handover-section">
          <h4>Message <span className="handover-optional">(optional)</span></h4>
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="e.g. Approved up to page 6 — the rest need checking, then print."
            rows={3}
            maxLength={MAX_MESSAGE}
            disabled={saving}
            aria-label="Message"
          />
        </div>

        <div className="handover-toggles">
          <div className="handover-toggle">
            <div className="checkbox-container">
              <div className="check light">
                <input id="handover-keep-me" type="checkbox" checked={keepMe} disabled={saving}
                  onChange={(e) => setKeepMe(e.target.checked)} />
                <label htmlFor="handover-keep-me"></label>
              </div>
            </div>
            <label htmlFor="handover-keep-me" className="handover-toggle-text">
              <strong>Keep me on this run</strong>
              <span>{keepMe
                ? 'Shared: you and they both work on it, and each sees the other\'s changes.'
                : 'Off: it moves to them and leaves your screen.'}</span>
            </label>
          </div>
          <div className="handover-toggle">
            <div className="checkbox-container">
              <div className="check light">
                <input id="handover-email" type="checkbox" checked={emailThem} disabled={saving}
                  onChange={(e) => setEmailThem(e.target.checked)} />
                <label htmlFor="handover-email"></label>
              </div>
            </div>
            <label htmlFor="handover-email" className="handover-toggle-text">
              <strong>Email them as well</strong>
              <span>They will also see it when they open Generate Statements.</span>
            </label>
          </div>
        </div>

        {error && (
          <div className="handover-outcome blocked" role="alert">
            <span className="material-symbols-rounded">error</span><span>{error}</span>
          </div>
        )}
      </div>
    </Modal>
  );
}

export default StatementHandOverDialog;
